/**
 * Canvas renderer for text overlays during video export.
 *
 * Ports TextOverlay.tsx (the on-screen React overlay) to Canvas 2D draw
 * commands so the exact same text appears, burned into the exported MP4.
 *
 * On-screen styling we replicate (TextOverlay.tsx):
 *   - 9-grid positioning (top/middle/bottom x left/center/right)
 *   - rounded "pill" behind each line: bg-black/40 + white/20 border
 *   - white text, whitespace-pre-wrap, max-width
 *   - container padding and gap between stacked items
 *
 * Every dimension comes from `textOverlayStyle` as a fraction of the 4:3 frame,
 * which is what keeps this identical to the player: the player resolves those
 * fractions in `cqw`, this resolves them in pixels against the 960-wide canvas.
 * Do not hardcode a size here — it will silently drift from what the user saw.
 */

import { Video } from '../types/Video';
import {
  TextPosition,
  OVERLAY_FONT_WEIGHT,
  overlayPixels,
} from './textOverlayStyle';

// Export canvas dimensions (must match videoExport COMPOSITE_*).
const CANVAS_W = 960;
const CANVAS_H = 720;

const {
  containerPad: CONTAINER_PAD,
  fontSize: FONT_SIZE,
  lineHeight: LINE_HEIGHT,
  pillPadX: PILL_PAD_X,
  pillPadY: PILL_PAD_Y,
  pillRadius: PILL_RADIUS,
  itemGap: ITEM_GAP,
} = overlayPixels(CANVAS_W);

const PILL_BG = 'rgba(0,0,0,0.4)';        // bg-black/40
const PILL_BORDER = 'rgba(255,255,255,0.2)'; // border-white/20
const TEXT_COLOR = '#ffffff';
const FONT_FAMILY =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Max width a pill may occupy (matches max-w-full minus container padding).
const MAX_PILL_W = CANVAS_W - CONTAINER_PAD * 2;
const MAX_TEXT_W = MAX_PILL_W - PILL_PAD_X * 2;

interface RenderableText {
  item: Video;
}

interface WrappedLine {
  text: string;
  width: number;
}

/** Word-wrap a single logical line to fit maxWidth, honoring explicit \n. */
function wrapText(ctx: CanvasRenderingContext2D, raw: string, maxWidth: number): WrappedLine[] {
  const out: WrappedLine[] = [];
  // Honor author-entered newlines first (whitespace-pre-wrap), then wrap each.
  for (const paragraph of raw.split('\n')) {
    if (paragraph === '') {
      out.push({ text: '', width: 0 });
      continue;
    }
    const words = paragraph.split(/\s+/);
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width > maxWidth && line) {
        out.push({ text: line, width: ctx.measureText(line).width });
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) out.push({ text: line, width: ctx.measureText(line).width });
  }
  return out;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** A pre-measured text item (one pill, possibly multiple wrapped lines). */
interface MeasuredPill {
  lines: WrappedLine[];
  pillW: number;
  pillH: number;
}

function measurePill(ctx: CanvasRenderingContext2D, text: string): MeasuredPill {
  const lines = wrapText(ctx, text, MAX_TEXT_W);
  const widest = lines.reduce((m, l) => Math.max(m, l.width), 0);
  const pillW = Math.min(MAX_PILL_W, widest + PILL_PAD_X * 2);
  const pillH = lines.length * LINE_HEIGHT + PILL_PAD_Y * 2;
  return { lines, pillW, pillH };
}

/**
 * Draw all active text overlays onto the export canvas for the current frame.
 * Call once per frame AFTER the video frame has been composited.
 *
 * @param ctx - 2D context of the 960x720 export canvas
 * @param textItems - text items active at this timeline position
 */
export function drawTextOverlays(
  ctx: CanvasRenderingContext2D,
  textItems: RenderableText[],
): void {
  if (textItems.length === 0) return;

  // Group by position (matches TextOverlay.tsx).
  const groups = new Map<TextPosition, MeasuredPill[]>();

  ctx.save();
  ctx.font = `${OVERLAY_FONT_WEIGHT} ${FONT_SIZE}px ${FONT_FAMILY}`;
  ctx.textBaseline = 'top';

  for (const { item } of textItems) {
    const text = item.custom_text ?? '';
    if (!text) continue;
    const position = (item.text_position || 'top-center') as TextPosition;
    const pill = measurePill(ctx, text);
    const list = groups.get(position) ?? [];
    list.push(pill);
    groups.set(position, list);
  }

  for (const [position, pills] of groups) {
    const vertical = position.split('-')[0] as 'top' | 'middle' | 'bottom';
    const horizontal = position.split('-')[1] as 'left' | 'center' | 'right';

    const stackH =
      pills.reduce((sum, p) => sum + p.pillH, 0) + ITEM_GAP * (pills.length - 1);

    // Vertical anchor of the whole stack within the padded container.
    let y: number;
    if (vertical === 'top') {
      y = CONTAINER_PAD;
    } else if (vertical === 'middle') {
      y = (CANVAS_H - stackH) / 2;
    } else {
      y = CANVAS_H - CONTAINER_PAD - stackH;
    }

    for (const pill of pills) {
      // Horizontal anchor per pill.
      let x: number;
      if (horizontal === 'left') {
        x = CONTAINER_PAD;
      } else if (horizontal === 'center') {
        x = (CANVAS_W - pill.pillW) / 2;
      } else {
        x = CANVAS_W - CONTAINER_PAD - pill.pillW;
      }

      // Pill background + border.
      roundRect(ctx, x, y, pill.pillW, pill.pillH, PILL_RADIUS);
      ctx.fillStyle = PILL_BG;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = PILL_BORDER;
      ctx.stroke();

      // Text lines. `textBaseline: 'top'` puts the em box at lineY, while CSS
      // centres that em box inside the taller line box — so add the half-leading
      // or the text rides high in the pill.
      ctx.fillStyle = TEXT_COLOR;
      let lineY = y + PILL_PAD_Y + (LINE_HEIGHT - FONT_SIZE) / 2;
      for (const line of pill.lines) {
        let textX: number;
        if (horizontal === 'left') {
          textX = x + PILL_PAD_X;
        } else if (horizontal === 'center') {
          textX = x + (pill.pillW - line.width) / 2;
        } else {
          textX = x + pill.pillW - PILL_PAD_X - line.width;
        }
        ctx.fillText(line.text, textX, lineY);
        lineY += LINE_HEIGHT;
      }

      y += pill.pillH + ITEM_GAP;
    }
  }

  ctx.restore();
}
